import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";
import CodeMirror from "@uiw/react-codemirror";
import { html } from "@codemirror/lang-html";

import "./PlaygroundEmbedPage.scss";
import ChatbotLayout from "../../chatbot/shared/ChatbotLayout";
import Message from "../../../i18n/Message";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CopyIcon,
  CheckIcon,
  UnplugIcon,
} from "lucide-react";

import { linkToMcpServer } from "../../../routes";
import { mcpPlaygroundSelectors } from "../store/mcpPlaygroundReducer";
import PlaygroundWidget from "../playground-widget/PlaygroundWidget";
import McpPlaygroundActions from "../store/mcpPlaygroundActions";
import backend from "../../../backend/backend";
import ToggleSwitch from "../../shared/toggle/ToggleSwitch";
import { UIModes } from "./playgroundSettings";
import { toast } from "react-toastify";
import { useTheme } from "../../../shared/theme/ThemeContext";

const PlaygroundEmbedPage = () => {
  const { playgroundId } = useParams();
  const intl = useIntl();
  const nav = useNavigate();
  const dispatch = useDispatch();
  const playground = useSelector(mcpPlaygroundSelectors.getPublicPlayground);
  const { isDark } = useTheme();
  const [uiMode, setUiMode] = useState(
    playground?.uiMode ?? (isDark ? UIModes.DARK : UIModes.LIGHT),
  );
  const [copied, setCopied] = useState(false);
  const copyTimeoutRef = useRef(null);

  const onUiModeChanged = (value) => {
    setUiMode(value);
  };

  const onCopyCode = () => {
    navigator.clipboard.writeText(embedCode);
    toast.success(intl.formatMessage({ id: "copy_code_success_message" }));
  };

  useEffect(() => {
    backend.getPublicPlayground(playgroundId).then((data) => {
      console.log("loaded public playground", data);
      dispatch({
        type: McpPlaygroundActions.PUBLIC_PLAYGROUND_LOADED,
        playground: data,
      });
      setUiMode(data?.uiMode ?? (isDark ? UIModes.DARK : UIModes.LIGHT));
    });
  }, [intl, dispatch, isDark]);

  console.log("Found playground: ", playground);

  const local = false;

  const host = local ? "http://localhost:5555" : "https://mcpshowcase.com";

  const embedCode = `
  <div class="mcpshowcase-widget" data-playground-id="${playground?.uid}" data-ui-mode="${uiMode}"></div>
  <script id="mcpshowcase-widget-loader" src="${host}/app/widgets/mcpshowcase-v1.js"></script>
  `.trim();

  if (playground) {
    return (
      <ChatbotLayout contentPanelClassName="playground_embed_page">
        <div className="title_row">
          <div className="breadcrumbs">
            <div onClick={() => nav(linkToMcpServer(playgroundId))}>
              <ChevronLeftIcon size={16} strokeWidth="2" color="currentColor" />
              <Message id="back_to_playground" />
            </div>
          </div>
          <div className="title_section">
            <div className="icon_container">
              <div className="icon_circle">
                <UnplugIcon size={24} color="currentColor" />
              </div>
            </div>
            <div className="title_column">
              <div className="title_value">{playground.name}</div>
              <div className="slug" title={playground.websiteUrl}>
                {playground.websiteUrl}
              </div>
            </div>
          </div>
        </div>
        <div className="embed_body">
          <div className="left_side">
            <div className="settings_container card">
              <div className="settings_title">
                <Message id="settings_configure_title" />
              </div>
              <div className="settings_body">
                <ToggleSwitch
                  onChange={onUiModeChanged}
                  value={uiMode}
                  optionLeft={{
                    label: "playground_ui_mode_light",
                    value: UIModes.LIGHT,
                  }}
                  optionRight={{
                    label: "playground_ui_mode_dark",
                    value: UIModes.DARK,
                  }}
                />
              </div>
              <div className="settings_title">
                <Message id="settings_copy_code_title" />
              </div>
              <div className="settings_body">
                <CodeMirror
                  value={embedCode}
                  height="80px"
                  extensions={[html()]}
                  readOnly={true}
                  editable={false}
                  autoFocus={false}
                  theme={"dark"}
                  basicSetup={{
                    lineNumbers: false,
                    foldGutter: false,
                    highlightActiveLine: false,
                    highlightActiveLineGutter: false,
                    lineWrapping: false,
                  }}
                />
                <p className="copy_code_description">
                  <Message id="copy_code_description" />
                </p>
                <button
                  className="btn bg-gradient-dark copy_code_button"
                  onClick={() => {
                    onCopyCode();
                    setCopied(true);
                    if (copyTimeoutRef.current) {
                      clearTimeout(copyTimeoutRef.current);
                    }
                    copyTimeoutRef.current = setTimeout(() => {
                      setCopied(false);
                    }, 3000);
                  }}
                  disabled={copied}
                >
                  {copied ? (
                    <>
                      <CheckIcon size={16} color="#fff" />
                      <Message id="copy_code_success" />
                    </>
                  ) : (
                    <>
                      <CopyIcon size={16} color="#fff" />
                      <Message id="copy_code" />
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
          <div className="arrow">
            <ChevronRightIcon size={48} color="currentColor" />
          </div>
          <div className="right_side">
            <div className="preview_container_wrapper">
              <div className="preview_container">
                <PlaygroundWidget playground={playground} uiMode={uiMode} />
              </div>
            </div>
          </div>
        </div>
      </ChatbotLayout>
    );
  } else {
    return null;
  }
};

export default PlaygroundEmbedPage;
